import { RefObject } from 'react';
import { createProvider } from '@frontend/store';
import {
  CalendarColumn,
  CalendarColumnGroup,
  CalendarEvent,
  CalendarColumnHeaderType,
  CalendarTimeIndicatorLines,
  CalendarColumnBodyVariant,
  CalendarGridDragCreateSelection,
  CalendarGridDragStyles,
} from '../types';

type State = {
  debug: boolean;
  headerType: CalendarColumnHeaderType;
  columnBodyVariant: CalendarColumnBodyVariant;
  groupIds: string[]; // keep this to maintain group order as we can't rely on object key order
  groupsById: Record<string, CalendarColumnGroup | undefined>;
  columnsById: Record<string, CalendarColumn | undefined>;
  eventsById: Record<string, CalendarEvent | undefined>;

  // derived state
  hasMultipleGroups: boolean; // can be used to determine if we need to show group headers, and also for some styling decisions
  canScrollLeft: boolean;
  canScrollRight: boolean;

  // other state
  timeIndicatorLines: CalendarTimeIndicatorLines;
  dragCreateSelection?: CalendarGridDragCreateSelection;

  // column reorder state
  isColumnReorderEnabled: boolean;
  isColumnReorderDragging: boolean;
  isColumnReorderActive: boolean;
  localColumnOrderByGroup: Record<string, string[] | undefined>;
  onColumnReorder?: (groupId: string, newColumnIds: string[]) => void;
  groupColumnWidth: number;
  selectedEventIds?: string[];
  onEventsSelect?: (eventIds: string[]) => void;

  // event card drag-and-drop
  isEventDragEnabled: boolean;
  dragSnapMinutes: number;
  onEventDragStartRef: RefObject<((eventId: string) => void) | undefined>;
  onEventDragEndRef: RefObject<
    | ((
        eventId: string,
        targetColumnId: string,
        targetGroupId: string,
        newStartMinutes: number,
        clearPreview: () => void
      ) => void)
    | undefined
  >;
  isEventDraggableRef: RefObject<((event: CalendarEvent) => boolean) | undefined>;
  // live position of the dragged event card (null when not dragging).
  // Kept alive after drag ends so the drop-target overlay remains visible while a
  // confirmation modal is open. Cleared via clearEventDragPreview() on confirm/cancel.
  eventDragPreview: { columnId: string; groupId: string; startMinutes: number; eventId: string } | null;

  // drag style + render customization
  dragStyles: CalendarGridDragStyles;

  // droppable columns
  enableDroppableColumns: boolean;
  renderEventCardRef: RefObject<((event: CalendarEvent) => React.ReactNode) | undefined>;
  /** Optional per-event-type tooltip content override. When provided, replaces the default EventTooltipContent. */
  renderEventTooltipRef: RefObject<((event: CalendarEvent) => React.ReactNode) | undefined>;
};

type Actions = {
  toggleDebug: () => void;
  setTimeIndicatorLines: (interval: CalendarTimeIndicatorLines) => void;
  setDragCreateSelection: (selection?: CalendarGridDragCreateSelection) => void;
  setLocalColumnOrder: (groupId: string, columnIds: string[]) => void;
  clearLocalColumnOrder: (groupId: string) => void;
  setIsColumnReorderDragging: (dragging: boolean) => void;
  setEventDragPreview: (
    preview: { columnId: string; groupId: string; startMinutes: number; eventId: string } | null
  ) => void;
  clearEventDragPreview: () => void;
  setGroupColumnWidth: (width: number) => void;
  setSelectedEventIds: (eventIds?: string[]) => void;
};

export type CalendarGridStore = State & Actions;

export const {
  Provider: CalendarGridStoreProvider,
  useStore: useCalendarGridStore,
  useNonReactiveStore,
} = createProvider<CalendarGridStore>()((set, get) => ({
  debug: false,
  headerType: 'default',
  columnBodyVariant: 'grid',
  groupIds: [],
  groupsById: {},
  columnsById: {},
  eventsById: {},

  hasMultipleGroups: false,
  canScrollLeft: false,
  canScrollRight: false,

  timeIndicatorLines: 'half-hourly',
  groupColumnWidth: 0,

  initWithMockData: () => {},

  isColumnReorderEnabled: false,
  isColumnReorderDragging: false,
  isColumnReorderActive: false,
  localColumnOrderByGroup: {},

  toggleDebug: () => set((state) => ({ debug: !state.debug })),
  isEventDragEnabled: false,
  dragSnapMinutes: 5,
  onEventDragStartRef: { current: undefined },
  onEventDragEndRef: { current: undefined },
  isEventDraggableRef: { current: undefined },
  renderEventCardRef: { current: undefined },
  renderEventTooltipRef: { current: undefined },
  eventDragPreview: null,

  dragStyles: {},

  enableDroppableColumns: false,

  setTimeIndicatorLines: (timeIndicatorLines) => set({ timeIndicatorLines }),
  setDragCreateSelection: (dragCreateSelection) => set({ dragCreateSelection }),
  setLocalColumnOrder: (groupId, columnIds) =>
    set((state) => ({ localColumnOrderByGroup: { ...state.localColumnOrderByGroup, [groupId]: columnIds } })),
  clearLocalColumnOrder: (groupId) =>
    set((state) => ({ localColumnOrderByGroup: { ...state.localColumnOrderByGroup, [groupId]: undefined } })),
  setIsColumnReorderDragging: (dragging) => set({ isColumnReorderDragging: dragging }),
  setGroupColumnWidth: (width) => set({ groupColumnWidth: width }),
  setSelectedEventIds: (selectedEventIds) => {
    set({ selectedEventIds });
    get().onEventsSelect?.(selectedEventIds ?? []);
  },
  setEventDragPreview: (eventDragPreview) => set({ eventDragPreview }),
  clearEventDragPreview: () => set({ eventDragPreview: null }),
}));
